import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {Col, FormGroup, Label, Row} from 'reactstrap';
import {SelectField} from 'src/components/react-select';
import Button from 'src/components/button';
import DataTable from 'src/components/dataTable';
import {BillingBlock} from 'src/core/data/api/BillingBlock';
import {formatCurrency, formatDateValue} from 'src/containers/financial-container/helpers';
import {useBillingBlocks} from './queries';
import {BlocksStatusFilter, defaultBlocksFilter, useBillingBlock} from './provider';
import {ListState, RefreshButton, Truncated} from './list-state';
import ReleaseDialog, {ReleaseMode} from './release-dialog';
import ConfirmDialog from './confirm-dialog';
import StatusBadge, {useEnumLabel} from './status-badge';
import {formatDateTime, orNotAvailable} from './format';
import styles from './billing-block.module.scss';

interface BlocksTabProps {
  readonly: boolean;
}

const statusOptions: {value: BlocksStatusFilter; labelId: string}[] = [
  {value: 'ACTIVE', labelId: 'billingBlockFilterActive'},
  {value: 'RELEASED', labelId: 'billingBlockFilterReleased'},
  {value: 'ALL', labelId: 'billingBlockFilterAll'},
];

const noop = () => undefined;

const BlocksTab = ({readonly}: BlocksTabProps) => {
  const intl = useIntl();
  const enumLabel = useEnumLabel();
  const {blocksFilter, setBlocksFilter} = useBillingBlock();
  const query = useBillingBlocks(blocksFilter);
  const [selected, setSelected] = useState<BillingBlock | null>(null);
  const [details, setDetails] = useState<BillingBlock | null>(null);
  const [action, setAction] = useState<{block: BillingBlock; mode: ReleaseMode} | null>(null);

  const isDefaultFilter = blocksFilter.status === 'ACTIVE';
  const changeFilter = (next: typeof blocksFilter) => {
    setSelected(null);
    setBlocksFilter(next);
  };
  const clearFilters = () => changeFilter(defaultBlocksFilter);
  const clearLabel = intl.formatMessage({id: 'billingBlockClearFilters'});

  const empty = (() => {
    if (isDefaultFilter) {
      return {
        message: intl.formatMessage({id: 'billingBlockEmptyActive'}),
        action: {label: intl.formatMessage({id: 'billingBlockShowReleased'}), onClick: () => changeFilter({...blocksFilter, status: 'RELEASED'})},
      };
    }
    if (blocksFilter.status === 'ALL') {
      return {message: intl.formatMessage({id: 'billingBlockEmptyBlocks'}), action: undefined};
    }
    return {message: intl.formatMessage({id: 'billingBlockEmptyFiltered'}), action: {label: clearLabel, onClick: clearFilters}};
  })();

  // DataTable searches and sorts the field values, so the readable text goes in the row itself.
  const rows = (query.data || []).map((block) => ({
    ...block,
    customerLabel: orNotAvailable(intl, block.customerName),
    deviceLabel: orNotAvailable(intl, block.deviceName),
    valueLabel: formatCurrency(intl, block.chargeValue),
    dueDateLabel: formatDateValue(intl, block.chargeDueDate),
    statusLabel: enumLabel('Status', block.status),
    commandLabel: enumLabel('CommandStatus', block.commandStatus),
    blockedAtLabel: formatDateTime(intl, block.blockedAt),
    releaseReasonLabel: block.releaseReason ? enumLabel('ReleaseReason', block.releaseReason) : '',
  }));
  const canRelease = !!selected && selected.status === 'ACTIVE';
  const actions = (
    <>
      <RefreshButton query={query} />
      <Button className="neutral" color="secondary" size="sm" disabled={!selected} onClick={() => setDetails(selected)}>
        {intl.formatMessage({id: 'billingBlockDetails'})}
      </Button>
      {!readonly && (
        <>
          <Button className="neutral" color="secondary" size="sm" disabled={!canRelease} onClick={() => selected && setAction({block: selected, mode: 'releaseAndExempt'})}>
            {intl.formatMessage({id: 'billingBlockReleaseAndExempt'})}
          </Button>
          <Button className="solid" color="theme" size="sm" disabled={!canRelease} onClick={() => selected && setAction({block: selected, mode: 'release'})}>
            {intl.formatMessage({id: 'billingBlockRelease'})}
          </Button>
        </>
      )}
    </>
  );

  return (
    <section className={styles.section}>
      <Row form>
        <Col md={3}>
          <FormGroup className="custom-formGroup">
            <Label for="billing-block-filter-status">{intl.formatMessage({id: 'billingBlockFilterStatus'})}</Label>
            <SelectField
              id="billing-block-filter-status"
              label={intl.formatMessage({id: 'billingBlockFilterStatus'})}
              value={blocksFilter.status}
              options={statusOptions.map(({value, labelId}) => ({id: value, title: intl.formatMessage({id: labelId})}))}
              onChange={(status: string) => changeFilter({...blocksFilter, status: status as BlocksStatusFilter})}
            />
          </FormGroup>
        </Col>
      </Row>
      <ListState query={query} rows={rows} emptyMessage={empty.message} emptyAction={empty.action} actions={actions}>
        {(list) => (
          <DataTable
            key={query.dataUpdatedAt}
            add={noop}
            edit={noop}
            remove={noop}
            showGroupButtons={false}
            keyField="id"
            list={list}
            onSelectRow={setSelected}
            extraHeaderEl={() => actions}
            headers={[
              {title: 'billingBlockColumnCustomer', field: 'customerLabel', dataSort: true, dataFormat: (cell: string) => <Truncated text={cell} />},
              {title: 'billingBlockColumnDevice', field: 'deviceLabel', dataSort: true, dataFormat: (cell: string) => <Truncated text={cell} />},
              {title: 'billingBlockColumnChargeValue', field: 'valueLabel'},
              {title: 'billingBlockColumnChargeDueDate', field: 'dueDateLabel'},
              {title: 'billingBlockColumnStatus', field: 'statusLabel', dataSort: true, dataFormat: (cell: string, row: BillingBlock) => <StatusBadge kind="Status" value={row.status} />},
              {title: 'billingBlockColumnCommandStatus', field: 'commandLabel', dataSort: true, dataFormat: (cell: string, row: BillingBlock) => <StatusBadge kind="CommandStatus" value={row.commandStatus} />},
              {title: 'billingBlockColumnBlockedAt', field: 'blockedAtLabel'},
              {title: 'billingBlockColumnReleaseReason', field: 'releaseReasonLabel', dataSort: true},
            ]}
          />
        )}
      </ListState>
      <ConfirmDialog
        open={!!details}
        title={intl.formatMessage({id: 'billingBlockDetailsTitle'}, {device: orNotAvailable(intl, details?.deviceName)})}
        description={details && (
          <dl className={styles.detailList} data-detail>
            <dt>{intl.formatMessage({id: 'billingBlockColumnCustomer'})}</dt>
            <dd>{orNotAvailable(intl, details.customerName)}</dd>
            <dt>{intl.formatMessage({id: 'billingBlockColumnDevice'})}</dt>
            <dd>{orNotAvailable(intl, details.deviceName)}</dd>
            <dt>{intl.formatMessage({id: 'billingBlockDetailCommandSentAt'})}</dt>
            <dd>{formatDateTime(intl, details.commandSentAt)}</dd>
            {details.status === 'RELEASED' && (
              <>
                <dt>{intl.formatMessage({id: 'billingBlockDetailReleasedAt'})}</dt>
                <dd>{formatDateTime(intl, details.releasedAt)}</dd>
                <dt>{intl.formatMessage({id: 'billingBlockDetailReleaseNote'})}</dt>
                <dd>{orNotAvailable(intl, details.releaseNote)}</dd>
                <dt>{intl.formatMessage({id: 'billingBlockDetailReleaseCommandStatus'})}</dt>
                <dd>
                  {details.releaseCommandStatus ?
                    <StatusBadge kind="ReleaseCommandStatus" value={details.releaseCommandStatus} /> :
                    orNotAvailable(intl, null)}
                </dd>
              </>
            )}
          </dl>
        )}
        cancelLabel={intl.formatMessage({id: 'billingBlockClose'})}
        onCancel={() => setDetails(null)}
      />
      <ReleaseDialog
        block={action?.block || null}
        mode={action?.mode || 'release'}
        onClose={() => {
          setAction(null);
          setSelected(null);
        }}
      />
    </section>
  );
};

export default BlocksTab;
